if (!customElements.get('td-collection-infusion-card')) {
  class TdCollectionInfusionCard extends HTMLElement {
    static get productBlockSelector() {
      return 'product-block';
    }

    static get defaultQuickbuyCloseMs() {
      return 700;
    }

    #resizeObserver = null;
    #productObservers = [];
    #debounceId = -1;
    #closeDelayId = -1;
    #quickbuySettling = false;
    #lockedMaxHeightPx = null;
    #boundQuickbuyBeforeOpen = null;
    #boundQuickbuyAfterClose = null;

    connectedCallback() {
      this.#boundQuickbuyBeforeOpen = this.#onQuickbuyBeforeOpen.bind(this);
      this.#boundQuickbuyAfterClose = this.#onQuickbuyAfterClose.bind(this);
      document.addEventListener('on:quickbuy:before-open', this.#boundQuickbuyBeforeOpen);
      document.addEventListener('on:quickbuy:after-close', this.#boundQuickbuyAfterClose);

      this.#resizeObserver = new ResizeObserver(() => {
        this.#scheduleUpdate();
      });
      this.#resizeObserver.observe(this);

      this.#scheduleUpdate();
    }

    disconnectedCallback() {
      document.removeEventListener('on:quickbuy:before-open', this.#boundQuickbuyBeforeOpen);
      document.removeEventListener('on:quickbuy:after-close', this.#boundQuickbuyAfterClose);
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
      this.#disconnectProductObservers();
      window.clearTimeout(this.#debounceId);
      window.clearTimeout(this.#closeDelayId);
      this.#debounceId = -1;
      this.#closeDelayId = -1;
      this.#quickbuySettling = false;
      this.#lockedMaxHeightPx = null;
    }

    #onQuickbuyBeforeOpen() {
      this.#quickbuySettling = false;
      this.#clearPendingUpdates();
      this.#commitMaxHeight();
    }

    #onQuickbuyAfterClose() {
      this.#quickbuySettling = true;
      this.#clearPendingUpdates();
      this.#reapplyLockedMaxHeight();

      window.clearTimeout(this.#closeDelayId);
      this.#closeDelayId = window.setTimeout(() => {
        this.#closeDelayId = -1;
        this.#quickbuySettling = false;
        this.#commitMaxHeight();
      }, this.#getQuickbuyCloseMs() + 50);
    }

    #clearPendingUpdates() {
      window.clearTimeout(this.#debounceId);
      this.#debounceId = -1;
    }

    #scheduleUpdate() {
      if (this.#quickbuySettling) return;

      const tallest = this.#measureRowProductHeight();
      const locked = this.#lockedMaxHeightPx;
      const isShrink = tallest === 0 || (locked != null && tallest > 0 && tallest < locked);

      if (isShrink) {
        this.#clearPendingUpdates();
        this.#commitMaxHeight();
        return;
      }

      this.#clearPendingUpdates();
      this.#debounceId = window.setTimeout(() => {
        this.#debounceId = -1;
        this.#commitMaxHeight();
      }, 50);
    }

    #getQuickbuyCloseMs() {
      const productBlock = this.#getRowProductBlocks()[0];
      if (!productBlock) return TdCollectionInfusionCard.defaultQuickbuyCloseMs;

      const quickbuyCont = productBlock.querySelector('.quickbuy-container');
      if (!quickbuyCont) return TdCollectionInfusionCard.defaultQuickbuyCloseMs;

      const seconds = parseFloat(getComputedStyle(quickbuyCont).transitionDuration);
      if (Number.isNaN(seconds) || seconds <= 0) {
        return TdCollectionInfusionCard.defaultQuickbuyCloseMs;
      }

      return seconds * 1000;
    }

    #getProductGrid() {
      return this.closest('.product-grid');
    }

    #getRowProductBlocks() {
      const grid = this.#getProductGrid();
      if (!grid) return [];

      const rowTop = this.offsetTop;
      return [...grid.querySelectorAll(TdCollectionInfusionCard.productBlockSelector)].filter(
        (productBlock) => productBlock.offsetTop === rowTop
      );
    }

    #measureRowProductHeight() {
      let tallest = 0;

      for (const productBlock of this.#getRowProductBlocks()) {
        if (productBlock.classList.contains('expanded')) continue;
        tallest = Math.max(tallest, productBlock.offsetHeight);
      }

      return tallest;
    }

    #applyMaxHeight(px) {
      this.#lockedMaxHeightPx = px;
      this.style.setProperty('--td-infusion-max-height', `${px}px`);
    }

    #reapplyLockedMaxHeight() {
      if (this.#lockedMaxHeightPx == null) return;
      this.style.setProperty('--td-infusion-max-height', `${this.#lockedMaxHeightPx}px`);
    }

    #clearMaxHeight() {
      this.#lockedMaxHeightPx = null;
      this.style.removeProperty('--td-infusion-max-height');
    }

    #commitMaxHeight() {
      const tallest = this.#measureRowProductHeight();

      if (tallest > 0) {
        this.#applyMaxHeight(tallest);
      } else if (this.#quickbuySettling) {
        this.#reapplyLockedMaxHeight();
      } else {
        this.#clearMaxHeight();
      }

      this.#observeRowProducts();
    }

    #disconnectProductObservers() {
      this.#productObservers.forEach((observer) => observer.disconnect());
      this.#productObservers = [];
    }

    #observeRowProducts() {
      this.#disconnectProductObservers();

      for (const productBlock of this.#getRowProductBlocks()) {
        const observer = new ResizeObserver(() => {
          this.#scheduleUpdate();
        });
        observer.observe(productBlock);
        this.#productObservers.push(observer);
      }
    }
  }

  customElements.define('td-collection-infusion-card', TdCollectionInfusionCard);
}
